/**
 * Where the login page may send a signed-in user's credentials or browser.
 *
 * The login page used to POST the access + refresh tokens to any `?callback=`
 * URL and follow any `?redirect=`, automatically, for an already signed-in
 * user — one crafted link took over the session. The only legitimate targets
 * are the Cortex CLI / preview server on the user's own machine, so anything
 * that isn't plain http on a loopback host with one of their known paths is
 * dropped.
 */

const LOOPBACK_HOSTS = new Set(["127.0.0.1", "localhost", "[::1]"]);

function loopbackUrl(raw: string | null, paths: readonly string[]): string | null {
  if (!raw) return null;
  let u: URL;
  try {
    u = new URL(raw);
  } catch {
    return null;
  }
  if (u.protocol !== "http:") return null;
  if (!LOOPBACK_HOSTS.has(u.hostname)) return null;
  if (u.username || u.password) return null;
  if (!paths.includes(u.pathname)) return null;
  return u.toString();
}

/** `?callback=` — the CLI's local token receiver (`/cli/callback`) or the
 * preview server's (`/preview/callback`). */
export function safeCliCallback(raw: string | null): string | null {
  return loopbackUrl(raw, ["/cli/callback", "/preview/callback"]);
}

/** `?redirect=` after approving a preview-server device login — back to
 * `http://localhost:<port>/preview/auth/complete?sid=…`. */
export function safePreviewRedirect(raw: string | null): string | null {
  return loopbackUrl(raw, ["/preview/auth/complete"]);
}

/** `?returnTo=` — an in-app path only (no scheme, no `//host`, no `/\host`). */
export function safeReturnTo(raw: string | null): string | null {
  if (!raw || !raw.startsWith("/") || raw.startsWith("//") || raw.startsWith("/\\")) return null;
  return raw;
}
